import { useState } from "react";

// Third Party
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useDebouncedValue } from "@mantine/hooks";

// Custom Hooks
import useUserToken from "@/hook/useUserToken";

// UI
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";

// Icons
import { ArrowLeft, Upload, Circle, CircleCheckBig } from "lucide-react";

// Utils
import { cn } from "@/lib/utils";

// Services
import { getUserList, createConversationGroup } from "@/services/chat";
import type { UserList } from "@/types/chat";
import { CHAT_AVATAR_EXTRA_SMALL } from "@/components/constants";
import { handleUploadFile } from "@/services/presign-url";

export const Route = createFileRoute(
	"/_layout-private/chat/_layout/group/mutate",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const user = useUserToken();
	const navigate = useNavigate();
	const { toast } = useToast();
	const queryClient = useQueryClient();

	// State
	const [search, setSearch] = useState("");
	const [groupName, setGroupName] = useState("");
	const [debounceSearch] = useDebouncedValue(search, 500);
	const [selectedFile, setSelectedFile] = useState();
	const [selectedSOEs, setSelectedSOEs] = useState<string[]>([]);

	// Query
	const { data, isLoading } = useQuery({
		queryKey: ["user-lists", debounceSearch, user?.data?.userChatID],
		queryFn: () =>
			getUserList({
				userId: user?.data?.userChatId,
				search: debounceSearch,
				role: "ADMIN"
			}),
	});

	// Mutation
	const createConversationGroupMutation = useMutation({
		mutationFn: createConversationGroup,
		onSuccess: (data) => {
			console.log("Res", data);

			queryClient.invalidateQueries({
				queryKey: ["conversationList"],
			});

			navigate({
				to: "/chat/group/$id",
				params: {
					id: data?._id,
				},
			});

			toast({
				title: "ສຳເລັດ",
				description: "ອັບໂຫຼດຂໍ້ມູນສໍາເລັດແລ້ວ",
			});
		},
		onError: (error) => {
			console.error("Upload failed:", error);

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
				variant: "destructive",
			});
		},
	});

	const uploadMutation = useMutation({
		mutationFn: handleUploadFile,
		onSuccess: (filename) => {
			console.log("Upload successful, filename:", filename);

			setSelectedFile(filename);

			toast({
				title: "ສຳເລັດ",
				description: "ອັບໂຫຼດຂໍ້ມູນສໍາເລັດແລ້ວ",
			});
		},
		onError: (error) => {
			console.error("Upload failed:", error);

			const fileInput = document.getElementById("fileUrl") as HTMLInputElement;
			if (fileInput) fileInput.value = "";

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
				variant: "destructive",
			});
		},
	});

	// Function
	const handleSubmit = () => {
		const payload = {
			conversationName: groupName,
			conversationImage: selectedFile,
			users: selectedSOEs,
			creator: user?.data?.userChatId,
		};

		console.log("Payload", payload);

		createConversationGroupMutation.mutate(payload);
	};

	console.log(data?.data, "User List");

	const lists = data?.data as UserList[];

	return (
		<div className="flex flex-col    w-full overflow-y-auto min-h-[calc(100vh-200px)] gap-4 ">
			<header className="flex relative justify-center items-center p-4 border-b">
				<Link to="/chat" className="flex absolute left-4 items-center text-sm">
					<ArrowLeft className="mr-2 w-[16px] h-[16px]" />
					<span>ກັບຄືນ</span>
				</Link>

				<h2 className="font-bold text-[18px]">ສ້າງກຸ່ມສົນທະນາໃໝ່</h2>
			</header>

			<section className="py-[12px] flex items-center gap-8 px-[24px] border-b relative">
				<div>
					<Label htmlFor="profile_image_url">ຮູບພາບກຸ່ມ</Label>
					<div className="w-[150px] h-[150px] rounded-full border border-dotted bg-zinc-50 border-zinc-200 overflow-hidden  relative flex items-center flex-col justify-center gap-[24px]">
						{selectedFile ? (
							<img
								alt="pre "
								src={S3_BUCKET_LINK + selectedFile}
								className="object-cover w-full h-full"
							/>
						) : (
							<>
								<Upload className="w-8 h-8" />
								<div className="text-center text-[12px]">
									<p>ອັບໂຫຼດ</p>
									<p className="w-full text-muted-foreground text-wrap">
										(ຂະໜາດບໍ່ເກີນ 10MB)
									</p>
								</div>{" "}
							</>
						)}

						<input
							type="file"
							name="profile_image_url"
							id="profile_image_url"
							accept="image/*"
							className="absolute inset-0 opacity-0 cursor-pointer"
							onChange={(e) => {
								if (e.target.files) {
									// setSelectedFile(e.target.files[0]);

									uploadMutation.mutate(e);
								}
							}}
						/>
					</div>
				</div>

				<div className="w-[250px]">
					<Label>
						ຕັ້ງຊື່ກຸ່ມສົນທະນາໃໝ່ <span className="text-red-500">*</span>
					</Label>

					<Input
						placeholder="ປ້ອນຊື່ກຸ່ມ..."
						required
						onChange={(e) => setGroupName(e.target.value)}
					/>
					<small className="text-muted-foreground">ຊື່ທີ່ປ້ອນຈະໄປສະແດງເປັນຊື່ກຸ່ມ</small>
				</div>

				<Button
					className="absolute top-4 right-4"
					disabled={
						uploadMutation.isPending ||
						groupName?.trim()?.length === 0 ||
						selectedSOEs?.length === 0 ||
						createConversationGroupMutation.isPending
					}
					onClick={handleSubmit}
				>
					{createConversationGroupMutation.isPending ? "ກຳລັງສ້າງກຸ່ມ" : "ບັນທຶກ"}
				</Button>
			</section>

			<div className="w-[65%] mx-auto">
				<Input
					onChange={(e) => setSearch(e.target.value)}
					placeholder="ຄົ້ນຫາຊື່ລັດວິສາຫະກິດ"
				/>
				<small className="text-muted-foreground">
					ລາຍຊື່ວິສາຫະກິດທັງໝົດ ({lists?.length}/{data?.total})
				</small>

				<div className="flex flex-col w-full h-[45vh] overflow-y-auto gap-4 mt-4">
					{isLoading
						? Array.from({ length: 10 }).map((_, i) => (
							<Skeleton key={i} className="h-[70px] rounded-lg" />
						))
						: lists.map((soe: UserList) => (
							<div
								key={soe._id}
								className={cn(
									"relative flex items-center gap-4 px-4 py-1 rounded-lg shadow cursor-pointer",
									selectedSOEs.includes(soe?._id) && "border border-primary ",
								)}
								onClick={() =>
									setSelectedSOEs((prev) => {
										if (prev.includes(soe?._id)) {
											return prev.filter((item) => item !== soe?._id);
										}
										return [...prev, soe?._id];
									})
								}
								onKeyDown={() => { }}
							>
								<Avatar className="w-[48px] h-[48px] flex items-center justify-center">
									<AvatarImage
										src={CHAT_AVATAR_EXTRA_SMALL + soe?.profileUrl}
									/>
									<AvatarFallback>{soe?.fullName?.charAt(0)}</AvatarFallback>
								</Avatar>

								<div className="text-sm">
									<h2>{soe?.fullName}</h2>
									<p className="text-muted-foreground">
										{soe?.phone || "*********"}
									</p>
								</div>

								{selectedSOEs.includes(soe?._id) ? (
									<CircleCheckBig className="absolute right-4 text-primary" />
								) : (
									<Circle className="absolute right-4 text-primary" />
								)}
							</div>
						))}
				</div>
			</div>
		</div>
	);
}
